import styles from "./stylesKpiCard.module.css";
import { Skeleton, Statistic, Tooltip } from "antd";
import CountUp from "react-countup";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

/**
 * @param {object} props
 * @param {string} props.title
 * Optional tooltip rendered as an "i" icon to the right of the title.
 * Used by KPIs whose meaning isn't obvious from the label alone (e.g.
 * "TOTAL USERS" → all-time vs date-ranged). Pass a plain string or any
 * React node accepted by antd's Tooltip.
 * @param {import('react').ReactNode} [props.titleTooltip]
 * @param {number|string} [props.value]
 * @param {boolean} [props.hugeValue]
 * @param {number|boolean|null} [props.percentage]
 * @param {boolean} [props.rate]
 * @param {string} [props.backgroundColor]
 * @param {boolean} [props.isApiRunning]
 * @param {string|false} [props.customColor]
 * @param {number} [props.maximumFractionDigits]
 * @param {number} [props.percentageMaximumFractionDigits]
 * @param {string|false} [props.borderColor]
 * @param {string} [props.currencySourceImage]
 * @param {boolean} [props.displayAsRawText]
 * When true, suppresses the percent-change badge (e.g. all-time Total Users).
 * @param {boolean} [props.hidePercentage]
 * Override the prefix currency symbol. Defaults to the configured symbol.
 * @param {string} [props.currencySymbol]
 */
function KpiCard({
  title,
  titleTooltip = null,
  value,
  hugeValue,
  percentage,
  rate = false,
  backgroundColor,
  isApiRunning = false,
  customColor = false,
  maximumFractionDigits = 2,
  percentageMaximumFractionDigits = undefined,
  borderColor = false,
  currencySourceImage = "",
  displayAsRawText = false,
  hidePercentage = false,
  currencySymbol = undefined,
}) {
  const configuredCurrencySymbol = useCurrencySymbol();
  const resolvedCurrencySymbol = currencySymbol ?? configuredCurrencySymbol;
  const formatter = (value) => (
    <CountUp end={value} separator="," decimal="." decimals={maximumFractionDigits} />
  );

  // Keep the sign outside the currency symbol / image so negatives render
  // as `-$500` rather than `$-500`. CountUp also animates cleaner when it
  // counts from 0 up to a positive target instead of down to a negative one.
  const numericValue = Number(value);
  const isNegative = Number.isFinite(numericValue) && numericValue < 0;
  const absValue = isNegative ? Math.abs(numericValue) : value;

  return (
    // `id="kpi-card"` is intentionally retained as a DOM-only anchor:
    // ~20 stylesheets across the app override the card via
    // `:global(#kpi-card)` (Manual Actions, Withdrawals, OverviewV2,
    // AffiliatePerformance, UtmReports, TopUsers, …). The internal
    // layout classes are hashed via CSS modules; specificity still
    // favours the id-based overrides, preserving today's behavior.
    <div
      id="kpi-card"
      className={styles.kpiCard}
      style={{ backgroundColor, border: borderColor }}
    >
      {isApiRunning && <Skeleton active />}
      {!isApiRunning && (
        <>
          <div className={styles.top}>
            <h3>{title}</h3>
            {titleTooltip ? (
              <Tooltip title={titleTooltip} placement="topRight">
                <span
                  className={styles.info}
                  role="img"
                  aria-label={typeof titleTooltip === "string" ? titleTooltip : "info"}
                >
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            ) : null}
          </div>
          <div className={styles.valueContainer}>
            <div
              className={`${styles.value} ${hugeValue ? styles.hugeValue : ""}`.trim()}
              style={customColor ? { color: customColor } : { color: "white" }}
            >
              {displayAsRawText ? (
                <span className="statistic-value">{value}</span>
              ) : (
                <>
                  {currencySourceImage && <img src={currencySourceImage} alt="Currency" className={styles.currencySourceImage}/>}
                  {isNegative && <span className="negative-sign">-</span>}
                  {rate || maximumFractionDigits === 0 || currencySourceImage ? "" : resolvedCurrencySymbol}{" "}
                  <div className="statistic-value">
                    <Statistic
                      value={absValue}
                      formatter={formatter}
                      valueStyle={
                        customColor ? { color: customColor } : { color: "white" }
                      }
                    />
                  </div>
                  {rate ? "%" : ""}
                </>
              )}
            </div>
            {!hidePercentage && (percentage || percentage === 0) && (
              <p>
                {" "}
                <span
                  className="percentage"
                  style={
                    percentage >= 0
                      ? { color: "#71BF8A" }
                      : { color: "#E74C3C" }
                  }
                >
                  {percentage >= 0 ? <span className="percentage-icon"><CaretUpOutlined /></span> : <span className="percentage-icon"><CaretDownOutlined /></span>}{" "}
                  <span className="percentage-value">
                    {Number(percentage).toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits:
                        percentageMaximumFractionDigits ?? maximumFractionDigits,
                      signDisplay: "never"
                    })}
                    %
                  </span>
                </span>
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default KpiCard;